import { reportError } from '../reportError';

/**
 * Reports GraphQL response errors as RUM custom errors — one per error, fingerprinted
 * `graphql:<operationName>:<extensions.code>` so each operation × code pair groups into a
 * single Error Tracking issue with new-issue/regression detection.
 *
 * Transport failures (`networkError`) are skipped: RUM resource tracking already records
 * them and Error Tracking excludes network-source errors by design.
 */
export const reportGraphQLError: ReportGraphQLError = async (params) => {
  const { graphQLErrors, networkError, context } = params;

  if (networkError !== undefined) return;

  const operationName = params.operationName ?? 'unknown';

  await Promise.all(
    graphQLErrors.map((graphQLError) => {
      const code = getErrorCode(graphQLError);

      return reportError(new Error(`GraphQL ${operationName}: ${graphQLError.message}`), {
        fingerprint: `graphql:${operationName}:${code}`,
        expected: EXPECTED_ERROR_CODES.has(code),
        context: { ...context, graphql: { operationName, code } },
      });
    }),
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getErrorCode(graphQLError: GraphQLErrorLike) {
  const code = graphQLError.extensions?.['code'];

  return typeof code === 'string' ? code : 'unknown';
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Reported with `expected: true` — grouped and visible, excludable from alert queries. */
const EXPECTED_ERROR_CODES = new Set(['UNAUTHENTICATED']);

// ─── Types ───────────────────────────────────────────────────────────────────

/** Structural subset of urql's CombinedError, so this lib carries no urql dependency. */
type ReportGraphQLError = (params: {
  operationName: string | undefined;
  graphQLErrors: readonly GraphQLErrorLike[];
  networkError: Error | undefined;
  context?: Record<string, unknown>;
}) => Promise<void>;

type GraphQLErrorLike = { message: string; extensions?: Record<string, unknown> };
